import { apiBaseUrl } from '../api/baseUrl';
import type { AppLocale } from '../i18n';

export type PethernityStatus = 'none' | 'active' | 'expired';

export type SessionUser = {
  id: string;
  email: string;
  locale?: AppLocale;
  emailVerified?: boolean;
  pethernity?: {
    active: boolean;
    expiresAt: string | null;
    status: PethernityStatus;
  };
  ads?: {
    enabled: boolean;
    clientId?: string | null;
  };
};

const userCookieName = 'petcem_user';
const sessionMaxAge = 60 * 60 * 24 * 7;
const listeners = new Set<(user: SessionUser | null) => void>();

let currentUser: SessionUser | null = readUserCookie();

function isSessionUser(value: unknown): value is SessionUser {
  if (!value || typeof value !== 'object') return false;
  const user = value as Record<string, unknown>;
  return typeof user.id === 'string' && typeof user.email === 'string';
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const part = document.cookie.split('; ').find((item) => item.startsWith(prefix));
  return part ? part.slice(prefix.length) : null;
}

function setUserCookie(user: SessionUser): void {
  document.cookie = `${userCookieName}=${encodeURIComponent(JSON.stringify(user))}; Max-Age=${sessionMaxAge}; Path=/; SameSite=Lax`;
}

function clearUserCookie(): void {
  document.cookie = `${userCookieName}=; Max-Age=0; Path=/; SameSite=Lax`;
}

function readUserCookie(): SessionUser | null {
  try {
    const raw = readCookie(userCookieName);
    if (!raw) return null;
    const parsed = JSON.parse(decodeURIComponent(raw));
    return isSessionUser(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function setCurrentUser(user: SessionUser | null): SessionUser | null {
  currentUser = user;
  if (user) setUserCookie(user);
  else clearUserCookie();
  listeners.forEach((cb) => cb(currentUser));
  return currentUser;
}

async function authRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  const res = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: 'include',
    headers,
  });

  const data = (await res.json().catch(() => null)) as { message?: string } | T | null;
  if (!res.ok) {
    const message = data && typeof data === 'object' && 'message' in data ? data.message : 'Request failed';
    throw new Error(message ?? 'Request failed');
  }

  return data as T;
}

export function getCurrentUser(): SessionUser | null {
  return currentUser ?? readUserCookie();
}

export function onUserChange(cb: (user: SessionUser | null) => void): () => void {
  listeners.add(cb);
  cb(getCurrentUser());
  return () => listeners.delete(cb);
}

export async function syncUserLocale(locale: AppLocale): Promise<void> {
  if (!getCurrentUser()) return;
  try {
    await authRequest<SessionUser>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify({ locale }),
    });
  } catch {
    /* best-effort */
  }
}

export async function refreshSession(): Promise<SessionUser | null> {
  try {
    const user = await authRequest<SessionUser>('/auth/me', { method: 'GET' });
    return setCurrentUser(user);
  } catch {
    return setCurrentUser(null);
  }
}

export async function startRegister(email: string, password: string): Promise<void> {
  const { getLocale } = await import('../i18n');
  await authRequest<{ message: string }>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, locale: getLocale() }),
  });
}

export async function verifyRegisterOtp(email: string, code: string): Promise<SessionUser> {
  const user = await authRequest<SessionUser>('/auth/register/verify', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
  const sessionUser = setCurrentUser(user)!;
  const { getLocale } = await import('../i18n');
  await syncUserLocale(getLocale());
  return sessionUser;
}

export async function resendRegisterOtp(email: string): Promise<void> {
  const { getLocale } = await import('../i18n');
  await authRequest<{ message: string }>('/auth/register/resend', {
    method: 'POST',
    body: JSON.stringify({ email, locale: getLocale() }),
  });
}

export async function login(email: string, password: string): Promise<SessionUser> {
  await authRequest<{ message: string }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  const sessionUser = (await refreshSession())!;
  const { getLocale } = await import('../i18n');
  await syncUserLocale(getLocale());
  return sessionUser;
}

export async function logout(): Promise<void> {
  await authRequest<{ message: string }>('/auth/logout', { method: 'POST' }).catch(() => null);
  setCurrentUser(null);
}

export async function requestPasswordReset(email: string): Promise<void> {
  await authRequest<{ message: string }>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(token: string, password: string): Promise<SessionUser> {
  await authRequest<{ message: string }>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
  return (await refreshSession())!;
}

export async function verifyEmail(token: string): Promise<SessionUser> {
  const user = await authRequest<SessionUser>('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  return setCurrentUser(user)!;
}

export async function resendVerificationEmail(): Promise<void> {
  await authRequest<{ message: string }>('/auth/resend-verification', {
    method: 'POST',
  });
}
